/**
 * Schemas for the `ship.*` tRPC procedures.
 *
 * `ship.list` returns the static vessel registry (filterable by outbreak slug
 * and tracking flag). `ship.getLatest` returns the most-recent AIS position
 * for a given MMSI (with a derived `isStale` flag — true when the latest
 * fix is older than 10 minutes). `ship.getTrack` returns a GeoJSON LineString
 * with timestamped properties for the vessel's recent track, with optional
 * downsampling so the wire payload stays bounded.
 *
 * MMSI is the AIS Maritime Mobile Service Identity — a 9-digit decimal
 * identifier broadcast by every Class A/B AIS transponder.
 */

import { z } from "zod";

/** AIS Maritime Mobile Service Identity — exactly 9 decimal digits. */
export const mmsiSchema = z
  .string()
  .regex(/^\d{9}$/u, "MMSI must be exactly 9 digits");

export const vesselSummarySchema = z.object({
  mmsi: z.string(),
  name: z.string(),
  flagCountryCode: z.string().length(2).nullable(),
  vesselType: z.string().nullable(),
  operator: z.string().nullable(),
  trackingEnabled: z.boolean(),
});
export type VesselSummary = z.infer<typeof vesselSummarySchema>;

export const shipListInputSchema = z.object({
  outbreakSlug: z.string().min(1).max(100).optional(),
  trackingEnabled: z.boolean().optional(),
});

export const shipListOutputSchema = z.array(vesselSummarySchema);

export const shipGetLatestInputSchema = z.object({
  mmsi: mmsiSchema,
});

export const shipPositionSchema = z.object({
  mmsi: z.string(),
  name: z.string(),
  position: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
  speedKnots: z.number().nullable(),
  headingDegrees: z.number().int().nullable(),
  recordedAt: z.date(),
  /** Seconds since the last fix; 0 means the fix landed this very second. */
  ageSeconds: z.number().int().min(0),
  /** True when ageSeconds > 600 (10 minutes). UI surfaces a warning badge. */
  isStale: z.boolean(),
});
export type ShipPosition = z.infer<typeof shipPositionSchema>;

export const shipGetLatestOutputSchema = shipPositionSchema.nullable();

export const shipTrackDownsampleSchema = z.enum(["auto", "minute", "hour"]);
export type ShipTrackDownsample = z.infer<typeof shipTrackDownsampleSchema>;

export const shipGetTrackInputSchema = z.object({
  mmsi: mmsiSchema,
  /** ISO 8601 — defaults to 24h ago when omitted. */
  since: z.coerce.date().optional(),
  downsample: shipTrackDownsampleSchema.default("auto"),
});

/**
 * GeoJSON LineString with one timestamp per coordinate (ordered by
 * `recordedAt` ascending). `properties.timestamps[i]` matches
 * `geometry.coordinates[i]` — clients can hover a track point to see when
 * the fix was recorded. Empty tracks return a `LineString` with an empty
 * coordinate array; the schema-level invariant is "ordered, parallel arrays".
 */
export const shipTrackSchema = z.object({
  type: z.literal("Feature"),
  geometry: z.object({
    type: z.literal("LineString"),
    /** [lng, lat] order — GeoJSON convention. */
    coordinates: z.array(z.tuple([z.number(), z.number()])),
  }),
  properties: z.object({
    mmsi: z.string(),
    name: z.string(),
    /** ISO 8601 timestamps; same length as `coordinates`. */
    timestamps: z.array(z.string()),
    pointCount: z.number().int().min(0),
    downsampled: z.boolean(),
  }),
});
export type ShipTrack = z.infer<typeof shipTrackSchema>;

export const shipGetTrackOutputSchema = shipTrackSchema;

/**
 * `ship.recentPositions` — the last N AIS positions for a vessel,
 * oldest -> newest. Drives the live-track trail polyline (default
 * 50 segments) and the freshness banner under the map. Every row
 * carries `verifiedAt` so the UI can render provenance.
 */
export const shipRecentPositionsInputSchema = z.object({
  mmsi: mmsiSchema,
  /** Number of trail points to return; capped at 200 to bound payload. */
  limit: z.number().int().min(1).max(200).default(50),
});
export type ShipRecentPositionsInput = z.infer<
  typeof shipRecentPositionsInputSchema
>;

export const shipTrailPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  speedKnots: z.number().nullable(),
  headingDegrees: z.number().int().nullable(),
  /** AIS broadcast timestamp (ISO 8601, string over the wire). */
  recordedAt: z.string(),
  /** Ingest-side wall-clock verification timestamp (ISO 8601). */
  verifiedAt: z.string(),
  /** Provenance — canonical URL of the upstream feed for this row. */
  sourceUrl: z.string(),
});
export type ShipTrailPoint = z.infer<typeof shipTrailPointSchema>;

export const shipRecentPositionsOutputSchema = z.object({
  mmsi: z.string(),
  name: z.string(),
  /** AIS destination from ShipStaticData (null when unknown). */
  nextPort: z.string().nullable(),
  /** AIS ETA (ISO 8601 string) — null when unknown. */
  eta: z.string().nullable(),
  /** Oldest -> newest. Empty array is a valid response. */
  points: z.array(shipTrailPointSchema),
});
export type ShipRecentPositionsOutput = z.infer<
  typeof shipRecentPositionsOutputSchema
>;
